import { useState } from 'preact/hooks';
import { useAuth } from '../../context/AuthContext.js';
import { api } from '../../services/api.js';
import {
  type IdentityNumberValue,
  identityNumberBody,
  identityNumberError,
} from '../../utils/identityNumber.js';
import { IdentityNumberField } from '../identity/IdentityNumberField.js';
import { Button } from './Button.js';
import { SupportLink } from './SupportLink.js';

/**
 * 366 (#1223, US2, design 1d/1e) — the one-time IC number.
 *
 * Shown by `AuthGuard` after the phone and campus steps while the server says the number is required (a customer with
 * none; staff are never asked), so new, Google and existing accounts are all asked the same way. Saved once: only staff
 * change it afterwards. Like the campus screen it always offers Sign out, and while staff impersonate a customer it
 * offers no entry (the server refuses one too).
 */
export function IdentityNumberRequired() {
  const { logout, refreshUser, isImpersonating, exitImpersonation } = useAuth();
  const [value, setValue] = useState<IdentityNumberValue>({ kind: 'mykad', number: '' });
  const [error, setError] = useState('');
  const [taken, setTaken] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    const invalid = identityNumberError(value);
    setTaken(false);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.put('/users/me/identity-number', identityNumberBody(value));
      await refreshUser();
    } catch (err) {
      const refusal = err as { code?: string; message?: string };
      if (refusal?.code === 'IDENTITY_NUMBER_LOCKED') {
        // Already saved (another device got there first): re-read, and the guard lets them in.
        await refreshUser();
        return;
      }
      if (refusal?.code === 'IDENTITY_NUMBER_TAKEN') setTaken(true);
      setError(refusal?.message || 'Could not save your IC number. Please try again.');
      setSaving(false);
    }
  }

  const signOut = (
    <button
      type="button"
      data-testid="identity-sign-out"
      onClick={() => logout()}
      className="mt-6 w-full text-sm text-gray-400 underline"
    >
      Sign out
    </button>
  );

  if (isImpersonating) {
    return (
      <div data-testid="identity-required" className="page-fill bg-bg px-4 pt-10">
        <h1 className="text-xl font-bold text-text mb-2">Add your IC number</h1>
        <p className="text-sm text-gray-400 mb-6">
          Only the customer can give their IC number. Staff can correct it from the customer’s page
          in the admin.
        </p>
        <Button size="lg" className="w-full" onClick={() => exitImpersonation()}>
          Stop impersonating
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="identity-required" className="page-fill bg-bg px-4 pt-10">
      <h1 className="text-xl font-bold text-text mb-2">Add your IC number</h1>
      <p className="text-sm text-gray-400 mb-6">
        We now ask every customer for their MyKad or passport number, once. It keeps one account per
        person.
      </p>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <IdentityNumberField
          value={value}
          label={null}
          onChange={(v) => {
            setValue(v);
            setError('');
            setTaken(false);
          }}
          error={error || undefined}
          errorAction={
            taken ? (
              <SupportLink className="text-secondary-bright underline">Contact us</SupportLink>
            ) : undefined
          }
        />
        <Button type="submit" size="lg" className="w-full" loading={saving}>
          Save
        </Button>
      </form>
      {signOut}
    </div>
  );
}
